import { useFeatureAccess } from "@src/auth/permissions/FeatureAccessContext";
import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";
import { Disposition } from "@src/backpack-console/pages/SiteDashboard/Documents/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import FetchClients, { ClientOutput } from "../client";

type ManualBill = ClientOutput["utilities"]["ManualBill"];

const manualBill = {
  useQueryAllRecentlyUploaded,
  useQueryAll,
  useDeleteManualBills,
  usePatchManualBill,
  useInvalidateManualBillQueries,
  downloadManualBill,
  useQueryManualBillPreview,
} as const;

export default manualBill;

const MANUAL_BILL_KEY = "utilities/manual-bills";
// endpoint requires {feature: "Utilities", access: "read"}
async function getAllManualBills(
  site_id: number,
  statuses: ManualBill["status"][],
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  const { data, error } = await FetchClients.utilities.GET(
    "/{site_id}/manual_bills",
    {
      params: {
        path: {
          site_id,
        },
        query: {
          statuses,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
  if (data) return data;
  throw new Error(error);
}

function useQueryAll<TSelect = ManualBill[]>(
  site_id: number,
  statuses: ManualBill["status"][],
  select?: (data?: ManualBill[]) => TSelect
) {
  const featureAccess = useFeatureAccess("Utilities");

  return useQueryWithAccessCheckLog(
    ["Utilities", "read"],
    (checkFeatureAccessHeader) =>
      getAllManualBills(site_id, statuses, checkFeatureAccessHeader),
    {
      queryKey: [MANUAL_BILL_KEY, site_id, statuses],
      select,
      enabled: !!featureAccess,
      staleTime: 1000 * 60,
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function deleteManualBill(
  {
    manual_bill_id,
    site_id,
  }: {
    manual_bill_id: number;
    site_id: number;
  },
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  return FetchClients.utilities.DELETE(
    "/{site_id}/manual_bills/{manual_bill_id}",
    {
      params: {
        path: {
          site_id: site_id,
          manual_bill_id,
        },
      },
      headers: checkFeatureAccessHeader,
    }
  );
}

function useDeleteManualBills(site_id: number) {
  const invalidateQueries = useInvalidateManualBillQueries(site_id);

  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    async (manualBillIds: number[] | number, checkFeatureAccessHeader) => {
      const manual_bill_ids = Array.isArray(manualBillIds)
        ? manualBillIds
        : [manualBillIds];
      try {
        for (const manual_bill_id of manual_bill_ids) {
          const { error } = await deleteManualBill(
            {
              manual_bill_id,
              site_id,
            },
            checkFeatureAccessHeader
          );
          if (error) throw new Error(error);
        }
      } catch (e) {
        throw new Error("An error occurred while deleting bill(s)", {
          cause: e,
        });
      }
    },
    {
      onSettled: () => {
        invalidateQueries();
      },
    }
  );
}

// endpoint requires {feature: "Utilities", access: "edit"}
async function patchManualBill(
  {
    siteId,
    fileId,
    filename,
  }: {
    siteId: number;
    fileId: number;
    filename: string;
  },
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  return FetchClients.utilities
    .PATCH("/{site_id}/manual_bills/{manual_bill_id}", {
      params: {
        path: {
          manual_bill_id: fileId,
          site_id: siteId,
        },
      },
      body: { filename },
      headers: checkFeatureAccessHeader,
    })
    .then((res) => res.data);
}

function usePatchManualBill(siteId: number) {
  const invalidateQueries = useInvalidateManualBillQueries(siteId);
  return useMutationWithAccessCheckLog(
    ["Utilities", "edit"],
    async (
      {
        fileId,
        filename,
      }: {
        fileId: number;
        filename: string;
      },
      checkFeatureAccessHeader
    ) =>
      patchManualBill({ siteId, fileId, filename }, checkFeatureAccessHeader),
    {
      onSettled: () => {
        invalidateQueries();
      },
    }
  );
}

type DownloadAttachmentsParams = {
  fileId: number;
  disposition?: Disposition;
};
// endpoint requires {feature: "Utilities", access: "read"}
async function downloadManualBill(
  { disposition, fileId }: DownloadAttachmentsParams,
  checkFeatureAccessHeader: CheckFeatureAccessHeader
) {
  return FetchClients.d3
    .GET(`/backpack_manual_bills/download`, {
      params: {
        query: {
          file_id: fileId,
          disposition,
        },
      },
      headers: checkFeatureAccessHeader,
      parseAs: "text",
    })
    .then((res) => res.data);
}

function useQueryManualBillPreview(fileId?: number) {
  return useQuery({
    queryKey: ["backpack-manual-utility-bill-preview", fileId],
    queryFn: async () => {
      const { data, error } = await FetchClients.d3.GET(
        `/backpack_manual_bills/preview`,
        {
          params: {
            query: {
              file_id: [fileId!],
            },
          },
        }
      );
      if (data) {
        return data[fileId!];
      }
      throw new Error(error);
    },
    enabled: Boolean(fileId),
  });
}

// We show these on the utilities account management page
const RECENTLY_UPDATED_STATUSES: ManualBill["status"][] = [
  "failure",
  "in_progress",
  "incomplete",
];
function useQueryAllRecentlyUploaded(site_id: number) {
  return useQueryAll(site_id, RECENTLY_UPDATED_STATUSES);
}

function useInvalidateManualBillQueries(siteId: number) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [MANUAL_BILL_KEY, siteId],
    });
}
